import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useBlocker } from '@tanstack/react-router'
import { Check, Pencil, Plus, RotateCcw, Save, Trash2, Undo2, X } from 'lucide-react'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'

import { actionKeys, capabilitiesQuery, putCapabilities, validationMessages, type CapabilityMap } from '@/api/actions'
import { isApiError } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Chip } from '@/components/ui/chip'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toast'

import { asObj, asStrings, isObj, type Obj } from './doc'
import { nameNormalize, SELECT_CLASS, TagInput } from './fields'
import { CAPABILITY_SECTIONS, mapCapabilities, useActionsMeta, type CapabilitySection } from './meta'

/**
 * "What things can do" (§11.1): the capability map as a friendly table. Each row says "when a thing
 * is X, it can …" (a kind of thing, a word in its name, a kind of puppet, an area of the place…).
 * Built-in rows come from the studio; changing or turning one off, or adding a new one, writes the
 * parent's overlay (`PUT /api/capabilities {base_rev, body}`), and "Undo my change" takes a row back
 * to the built-in. Nothing is saved until "Save changes".
 */

interface SectionInfo {
  label: string
  /** "When a thing …" — completes the sentence before the key. */
  when: string
  placeholder: string
}

const SECTION_INFO: Record<CapabilitySection, SectionInfo> = {
  categories: { label: 'Kind of thing', when: 'is in the catalog group', placeholder: 'food_and_drink' },
  keywords: { label: 'Word in its name', when: 'has the word', placeholder: 'apple' },
  slugs: { label: 'Exactly this thing', when: 'is exactly', placeholder: 'red_ball' },
  kinds: { label: 'Kind of puppet', when: 'is a', placeholder: 'person' },
  plans: { label: 'Body shape', when: 'has the body shape', placeholder: 'quadruped' },
  types: { label: 'Type of participant', when: 'is a', placeholder: 'character' },
  clips: { label: 'Knows the move', when: 'knows the move', placeholder: 'swim' },
  sockets: { label: 'Has the body part', when: 'has a', placeholder: 'mouth' },
  rest_surfaces: { label: 'Rests on', when: 'rests on', placeholder: 'water' },
  region_kinds: { label: 'Area of the place', when: 'is the area', placeholder: 'water' },
  anchor_kinds: { label: 'Spot of the place', when: 'is the spot', placeholder: 'door' },
  affords: { label: 'The place offers', when: 'offers', placeholder: 'sit' },
  exclude: { label: 'Never able to', when: 'has the word (never able to)', placeholder: 'penguin' },
}

const PAGE = 60

interface Row {
  section: CapabilitySection
  key: string
  caps: string[]
  /** `builtin`: untouched; `mine`: in the overlay (added or changed); `off`: overlay says []; `undo`: back to built-in on save. */
  state: 'builtin' | 'mine' | 'off' | 'undo'
  /** A row added here and not saved yet (nothing under it): "Remove" instead of "Undo my change". */
  added: boolean
}

const words = (s: string): string => s.replace(/_/g, ' ')

function cleanOverlay(o: CapabilityMap | null | undefined): Obj {
  const out: Obj = { ...(o ?? {}) }
  for (const k of ['rev', 'updated_at', 'version']) delete out[k]
  return out
}

export function CapabilitiesEditor({ q = '' }: { q?: string }) {
  const qc = useQueryClient()
  const doc = useQuery(capabilitiesQuery)
  const { meta } = useActionsMeta()
  const saved = useMemo(() => cleanOverlay(doc.data?.overlay), [doc.data?.overlay])
  const [draft, setDraft] = useState<Obj>(saved)
  const [filter, setFilter] = useState<CapabilitySection | 'all'>('all')
  const [shown, setShown] = useState(PAGE)
  const [editing, setEditing] = useState<string | null>(null)
  const [conflict, setConflict] = useState<number | null | undefined>(undefined)
  // A fresh server copy (after a save or "Get the latest") replaces the draft.
  useEffect(() => setDraft(saved), [saved])

  const merged = asObj(doc.data?.capabilities)
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved)
  // Unsaved rows: ask before leaving the page or the tab (searching the table is fine).
  const unsaved = useRef(false)
  unsaved.current = dirty
  const shouldBlockFn = useCallback(
    ({ current, next }: { current: { pathname: string; search: unknown }; next: { pathname: string; search: unknown } }) =>
      unsaved.current && (current.pathname !== next.pathname || tabOf(current.search) !== tabOf(next.search)),
    [],
  )
  const enableBeforeUnload = useCallback(() => unsaved.current, [])
  const blocker = useBlocker({ shouldBlockFn, enableBeforeUnload, withResolver: true })
  const vocabulary = asObj(merged.vocabulary)
  const allCaps = useMemo(() => [...new Set([...mapCapabilities(merged), ...meta.capabilities])].sort(), [merged, meta.capabilities])

  const rows = useMemo(() => {
    const out: Row[] = []
    for (const section of CAPABILITY_SECTIONS) {
      const base = asObj(merged[section])
      const mine = asObj(draft[section])
      const was = asObj(saved[section])
      const keys = [...new Set([...Object.keys(base), ...Object.keys(mine)])]
      for (const key of keys) {
        if (key in mine) {
          const caps = asStrings(mine[key])
          out.push({ section, key, caps, state: caps.length === 0 && key in base ? 'off' : 'mine', added: !(key in base) })
        } else if (key in was) {
          // Saved as the parent's, now undone: shows what it is until saved.
          out.push({ section, key, caps: asStrings(base[key]), state: 'undo', added: false })
        } else {
          out.push({ section, key, caps: asStrings(base[key]), state: 'builtin', added: false })
        }
      }
    }
    // A row just added goes on top: at the end of its kind it would land beyond "Show more".
    return [...out.filter((r) => r.added), ...out.filter((r) => !r.added)]
  }, [merged, draft, saved])

  const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const visible = rows.filter((r) => (filter === 'all' || r.section === filter) && terms.every((w) => r.key.includes(w) || r.caps.some((c) => c.includes(w)) || SECTION_INFO[r.section].label.toLowerCase().includes(w)))
  const mineCount = rows.filter((r) => r.state !== 'builtin').length

  const setRow = (section: string, key: string, caps: string[] | undefined): void => {
    const table = { ...asObj(draft[section]) }
    if (caps === undefined) delete table[key]
    else table[key] = caps
    const next = { ...draft }
    if (Object.keys(table).length) next[section] = table
    else delete next[section]
    setDraft(next)
  }

  const mutation = useMutation({
    mutationFn: ({ rev, body }: { rev: number; body: Obj }) => putCapabilities(rev, body),
    onSuccess: (next) => {
      qc.setQueryData(actionKeys.capabilities(), next)
      void qc.invalidateQueries({ queryKey: actionKeys.meta() })
      setConflict(undefined)
      toast.success('Saved', { description: 'New stories use the new list.' })
    },
    onError: (err) => {
      if (isApiError(err) && err.status === 409) setConflict(typeof err.details.current_rev === 'number' ? err.details.current_rev : null)
    },
  })

  if (doc.isPending) return <p className="text-ink-2">Opening the list…</p>
  if (doc.isError) {
    return (
      <div className="card flex flex-col items-start gap-3 p-6" role="alert">
        <p className="text-ink">The list of what things can do could not be loaded.</p>
        <Button variant="secondary" onClick={() => void doc.refetch()}>
          Try again
        </Button>
      </div>
    )
  }

  const messages = mutation.error && !(isApiError(mutation.error) && mutation.error.status === 409) ? validationMessages(mutation.error) : []

  return (
    <div className="flex flex-col gap-4" data-testid="capabilities-editor">
      <p className="max-w-3xl text-sm text-ink-2">
        Stories decide what a thing can do from these rows: <span className="text-ink">“When a thing has the word <em>ball</em> in its name, it can be thrown, kicked and rolled.”</span> Change a row, turn one off, or add your own.
      </p>

      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Show rows about">
        <Chip size="sm" selected={filter === 'all'} onClick={() => setFilter('all')}>
          All
        </Chip>
        {CAPABILITY_SECTIONS.map((s) => (
          <Chip key={s} size="sm" selected={filter === s} onClick={() => setFilter(filter === s ? 'all' : s)}>
            {SECTION_INFO[s].label}
          </Chip>
        ))}
      </div>

      <AddRow allCaps={allCaps} meanings={vocabulary} onAdd={(section, key, caps) => setRow(section, key, caps)} />

      <div className="card overflow-x-auto p-0">
        <table className="w-full min-w-[40rem] border-collapse text-sm" aria-label="What things can do">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-3">
              <th scope="col" className="px-4 py-2 font-semibold">
                When a thing…
              </th>
              <th scope="col" className="px-4 py-2 font-semibold">
                …it can
              </th>
              <th scope="col" className="px-4 py-2 font-semibold">
                From
              </th>
              <th scope="col" className="px-4 py-2">
                <span className="sr-only">Change</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.slice(0, shown).map((r) => {
              const rowId = `${r.section}:${r.key}`
              return (
                <CapRow
                  key={rowId}
                  row={r}
                  editing={editing === rowId}
                  allCaps={allCaps}
                  meanings={vocabulary}
                  onEdit={() => setEditing(rowId)}
                  onDone={(caps) => {
                    setEditing(null)
                    if (caps && JSON.stringify(caps) !== JSON.stringify(r.caps)) setRow(r.section, r.key, caps)
                  }}
                  onOff={() => setRow(r.section, r.key, [])}
                  onUndo={() => setRow(r.section, r.key, undefined)}
                />
              )
            })}
          </tbody>
        </table>
        {visible.length === 0 ? <p className="p-4 text-sm text-ink-2">No rows match.</p> : null}
        {visible.length > shown ? (
          <div className="border-t border-line p-3">
            <Button variant="ghost" size="sm" onClick={() => setShown((n) => n + PAGE)}>
              Show more ({visible.length - shown} left)
            </Button>
          </div>
        ) : null}
      </div>

      <SizeRules rules={Array.isArray(merged.size) ? merged.size : []} />

      {conflict !== undefined ? (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-warn/40 bg-warn-soft p-3 text-sm text-ink" role="alert" data-testid="capabilities-conflict">
          <span className="mr-auto">Someone else saved this list while you were changing it.</span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setConflict(undefined)
              void doc.refetch()
            }}
          >
            Get the latest
          </Button>
          <Button variant="ghost" size="sm" onClick={() => conflict !== null && mutation.mutate({ rev: conflict, body: draft })} disabled={conflict === null}>
            Save mine anyway
          </Button>
        </div>
      ) : null}
      {messages.length ? (
        <div className="rounded-xl border border-danger/40 bg-danger-soft p-3 text-sm text-danger" role="alert" data-testid="capabilities-errors">
          <p className="font-medium">The studio did not accept it:</p>
          <ul className="mt-1 list-disc pl-5">
            {messages.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="sticky z-10 flex flex-wrap items-center gap-3 rounded-2xl border border-line bg-paper/95 p-3 shadow-pop backdrop-blur" style={{ bottom: 'calc(var(--shell-bottom, 40px) + 0.5rem)' }}>
        <p className="text-sm text-ink-2" role="status">
          {dirty ? 'You have changes that are not saved yet.' : mineCount ? `${mineCount} of your own rows.` : 'All rows are the studio’s own.'}
        </p>
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" disabled={!dirty || mutation.isPending} onClick={() => setDraft(saved)}>
            <RotateCcw aria-hidden />
            Undo my changes
          </Button>
          <Button variant="primary" disabled={!dirty} loading={mutation.isPending} onClick={() => mutation.mutate({ rev: doc.data?.rev ?? 0, body: draft })} data-testid="capabilities-save">
            {mutation.isPending ? null : <Save aria-hidden />}
            Save changes
          </Button>
        </div>
      </div>

      <Dialog open={blocker.status === 'blocked'} onOpenChange={(o) => (!o ? blocker.reset?.() : undefined)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Leave without saving?</DialogTitle>
            <DialogDescription>Your changes to what things can do are not saved yet.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => blocker.reset?.()}>
              Stay
            </Button>
            <Button variant="danger" onClick={() => blocker.proceed?.()} data-testid="capabilities-leave">
              Leave anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const tabOf = (search: unknown): unknown => (isObj(search) ? search.tab : undefined)

function CapRow({
  row,
  editing,
  allCaps,
  meanings,
  onEdit,
  onDone,
  onOff,
  onUndo,
}: {
  row: Row
  editing: boolean
  allCaps: string[]
  meanings: Obj
  onEdit(): void
  onDone(caps: string[] | null): void
  onOff(): void
  onUndo(): void
}) {
  const [caps, setCaps] = useState(row.caps)
  useEffect(() => setCaps(row.caps), [row.caps, editing])
  const id = useId()
  const info = SECTION_INFO[row.section]
  const name = `${info.label.toLowerCase()} “${words(row.key)}”`
  const meaning = (c: string): string | undefined => (typeof meanings[c] === 'string' ? (meanings[c] as string) : undefined)
  return (
    <tr className="border-b border-line align-top last:border-0" data-testid="cap-row" data-section={row.section} data-key={row.key} data-state={row.state}>
      <td className="px-4 py-2.5">
        <span className="text-ink-3">{info.when} </span>
        <span className="font-medium text-ink">{words(row.key)}</span>
      </td>
      <td className="px-4 py-2.5">
        {editing ? (
          <div className="flex flex-col gap-2">
            <label htmlFor={id} className="sr-only">
              What a thing with the {name} can do
            </label>
            <TagInput ctrl={{ id, invalid: false, describedBy: undefined }} values={caps} onChange={setCaps} suggestions={allCaps} labelOf={words} titleOf={meaning} placeholder="Add an ability…" />
            <div className="flex gap-2">
              <Button size="sm" variant="primary" onClick={() => onDone(caps)}>
                <Check aria-hidden />
                Done
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onDone(null)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : row.caps.length ? (
          <ul className="flex flex-wrap gap-1" aria-label="Can">
            {row.caps.map((c) => (
              <li key={c} className="rounded-full bg-paper-2 px-2 py-0.5 text-xs text-ink" title={meaning(c)}>
                {words(c)}
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-xs text-ink-3">{row.state === 'off' ? 'Nothing (turned off)' : 'Nothing'}</span>
        )}
      </td>
      <td className="px-4 py-2.5">
        {row.state === 'builtin' ? <Badge tone="neutral">Built-in</Badge> : null}
        {row.state === 'mine' ? <Badge tone="accent">Yours</Badge> : null}
        {row.state === 'off' ? <Badge tone="warn">Turned off</Badge> : null}
        {row.state === 'undo' ? <Badge tone="info">Back to built-in when saved</Badge> : null}
      </td>
      <td className="px-4 py-2.5">
        {editing ? null : (
          <div className="flex justify-end gap-0.5">
            <Button variant="ghost" size="sm" onClick={onEdit} aria-label={`Change ${name}`}>
              <Pencil aria-hidden />
              Change
            </Button>
            {row.state === 'builtin' ? (
              <Button variant="ghost" size="sm" onClick={onOff} aria-label={`Turn off ${name}`}>
                <X aria-hidden />
                Turn off
              </Button>
            ) : row.state === 'undo' ? null : (
              <Button variant="ghost" size="sm" onClick={onUndo} aria-label={row.added ? `Remove ${name}` : `Undo my change to ${name}`}>
                {row.added ? <Trash2 aria-hidden /> : <Undo2 aria-hidden />}
                {row.added ? 'Remove' : 'Undo my change'}
              </Button>
            )}
          </div>
        )}
      </td>
    </tr>
  )
}

function AddRow({ allCaps, meanings, onAdd }: { allCaps: string[]; meanings: Obj; onAdd(section: CapabilitySection, key: string, caps: string[]): void }) {
  const [section, setSection] = useState<CapabilitySection>('keywords')
  const [key, setKey] = useState('')
  const [caps, setCaps] = useState<string[]>([])
  const secId = useId()
  const keyId = useId()
  const capsId = useId()
  const ok = nameNormalize(key).length > 0 && caps.length > 0
  return (
    <form
      className="card flex flex-col gap-3 p-4 md:flex-row md:items-end"
      aria-label="Add a row"
      data-testid="cap-add"
      onSubmit={(e) => {
        e.preventDefault()
        if (!ok) return
        onAdd(section, nameNormalize(key), caps)
        setKey('')
        setCaps([])
      }}
    >
      <div className="flex flex-col gap-1.5">
        <label htmlFor={secId} className="text-sm font-medium text-ink">
          When a thing…
        </label>
        <select id={secId} className={SELECT_CLASS} value={section} onChange={(e) => setSection(e.target.value as CapabilitySection)}>
          {CAPABILITY_SECTIONS.map((s) => (
            <option key={s} value={s}>
              {SECTION_INFO[s].when}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={keyId} className="text-sm font-medium text-ink">
          This
        </label>
        <Input id={keyId} fieldSize="sm" value={key} placeholder={SECTION_INFO[section].placeholder} onChange={(e) => setKey(e.target.value)} className="font-mono" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <label htmlFor={capsId} className="text-sm font-medium text-ink">
          …it can
        </label>
        <TagInput
          ctrl={{ id: capsId, invalid: false, describedBy: undefined }}
          values={caps}
          onChange={setCaps}
          suggestions={allCaps}
          labelOf={words}
          titleOf={(c) => (typeof meanings[c] === 'string' ? (meanings[c] as string) : undefined)}
          placeholder="holdable, floats…"
        />
      </div>
      <Button type="submit" variant="secondary" disabled={!ok}>
        <Plus aria-hidden />
        Add the row
      </Button>
    </form>
  )
}

function SizeRules({ rules }: { rules: unknown[] }) {
  if (rules.length === 0) return null
  return (
    <details className="card p-4">
      <summary className="cursor-pointer select-none text-sm font-semibold text-ink">Size rules ({rules.length})</summary>
      <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-ink-2">
        {rules.filter(isObj).map((r, i) => (
          <li key={i}>
            <span className="text-ink">{words(String(r.capability ?? ''))}</span>: {r.op === 'min' ? 'at least' : 'at most'} {Math.round(Number(r.ratio ?? 0) * 100)}% as tall as the {String(r.of ?? 'tallest')} character
            {r.grant ? ' (things this size get it)' : ''}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-ink-3">These come with the studio.</p>
    </details>
  )
}
